/* eslint-disable camelcase */
import {
	OnboardingType,
	LeadGrowthOpportunitiesV3,
	MemberGrowthOpportunitiesV3,
	TeamGrowthOpportunityV3
} from "@teaming/common-enums";

const leadFactory = ( { currentUser, amplitude, intercom, } ) => {
	const personalGrowthOpportunityV3Text = {
		[ LeadGrowthOpportunitiesV3.COACHING ]: "coaching and 1:1s",
		[ LeadGrowthOpportunitiesV3.VISION ]: "setting vision and strategy for the team",
		[ LeadGrowthOpportunitiesV3.COMMUNICATION ]: "communication, listening, and information sharing",
		[ LeadGrowthOpportunitiesV3.DEVELOPMENT ]: "career development and team performance",
		[ LeadGrowthOpportunitiesV3.RESPECT ]: "creating a respectful team environment",
		[ LeadGrowthOpportunitiesV3.DELEGATION ]: "empowering team member through delegation",
		[ LeadGrowthOpportunitiesV3.DECISIONS ]: "decision-making",
		[ LeadGrowthOpportunitiesV3.CONFLICT ]: "handling conflict",
		[ LeadGrowthOpportunitiesV3.OTHER ]: "other",
	};

	const teamGrowthOpportunityV3Text = {
		[ TeamGrowthOpportunityV3.CONNECTIONS ]: "building connections with and among team members",
		[ TeamGrowthOpportunityV3.FEEDBACK ]: "sharing and receiving feedback",
		[ TeamGrowthOpportunityV3.TEAM_PERFORMANCE ]: "improving team performance",
		[ TeamGrowthOpportunityV3.EFFECTIVE_MEETINGS ]: "having effective team or 1:1 meetings",
		[ TeamGrowthOpportunityV3.COACHING ]: "coaching and developing team members",
		[ TeamGrowthOpportunityV3.OTHER ]: "other",
	};

	const prefix = "Lead Onboarding -";

	return {
		started( teamId ) {
			intercom.update( currentUser(), {
				onboarding_path_type: OnboardingType.LEAD,
			} );
			amplitude.event( "Onboarding Started", {
				path: "Lead",
				associated_team: teamId,
			} );
		},
		completed( teamId ) {
			amplitude.event( "Onboarding Completed", {
				path: "Lead",
				associated_team: teamId,
			} );
			intercom.update( currentUser(), {
				onboarding_completed: true,
			} );
		},
		welcome() {
			amplitude.event( `${ prefix } Welcome`, {}, true );
		},
		/** @deprecated */
		discoverLeadershipStyle() {
			amplitude.event( `${ prefix } Discover Leadership Style` );
		},
		/** @deprecated */
		leadershipStyleAssessment() {
			amplitude.event( `${ prefix } Leadership Style Assessment` );
		},
		/** @deprecated */
		leadershipStyleResults() {
			amplitude.event( `${ prefix } Leadership Style Results` );
		},
		/** @deprecated */
		inviteYourTeam() {
			amplitude.event( `${ prefix } Invite Your Team` );
		},
		/** @deprecated */
		leadershipExperienceIntel() {
			amplitude.event( `${ prefix } Leadership Experience Intel` );
		},
		/** @deprecated */
		teamIntel() {
			amplitude.event( `${ prefix } Team Intel` );
		},
		/** @deprecated */
		teamPriority() {
			amplitude.event( `${ prefix } Team Priority` );
		},
		/** @deprecated */
		teamPriorityValueProp() {
			amplitude.event( `${ prefix } Team Priority Value Prop` );
		},
		/** @deprecated */
		calendarIntegration() {
			amplitude.event( `${ prefix } Calendar Integration` );
		},
		/** @deprecated */
		teamSettings() {
			amplitude.event( `${ prefix } Team Settings` );
		},
		growthOpportunities() {
			amplitude.event( `${ prefix } Personal Growth Priority`, {
				location: "Lead Onboarding",
			}, true );
		},
		growthOpportunity( { growthOpportunity, onboardingVersion = 3, } ) {
			const textMaps = {
				3: personalGrowthOpportunityV3Text,
			};
			const textMap = textMaps[ onboardingVersion ] || {};
			const text = textMap[ growthOpportunity ];

			if ( !text ) {
				// eslint-disable-next-line no-console
				return console.warn( `missing growth opportunity text for ${ growthOpportunity }; will not update Intercom` );
			}

			intercom.update( currentUser(), {
				team_lead_personal_growth_priority: growthOpportunity,
				team_lead_personal_growth_priority_display_text: text,
			} );
		},
		teamGrowthOpportunities() {
			amplitude.event( `${ prefix } Team Growth Priority`, {
				location: "Lead Onboarding",
			}, true );
		},
		teamGrowthOpportunity( { growthOpportunity, onboardingVersion = 3, } ) {
			const textMaps = {
				3: teamGrowthOpportunityV3Text,
			};
			const textMap = textMaps[ onboardingVersion ] || {};
			const text = textMap[ growthOpportunity ];

			if ( !text ) {
				// eslint-disable-next-line no-console
				return console.warn( `missing team growth opportunity text for ${ growthOpportunity }; will not update Intercom` );
			}

			intercom.update( currentUser(), {
				team_lead_initial_team_growth_priority: growthOpportunity,
				team_lead_initial_team_growth_priority_display_text: text,
			} );
		},
		/** @deprecated */
		workStyleAssessmentStarted() {
			amplitude.event( "Work Style Assessment Started", {
				location: "Lead Onboarding",
			} );
		},
		/** @deprecated */
		workStyleAssessmentCompleted( { moniker, } ) {
			amplitude.event( "Work Style Assessment Completed", {
				location: "Lead Onboarding",
			} );

			intercom.update( currentUser(), {
				work_style_type: moniker,
			} );
		},
	};
};

const memberFactory = ( { currentUser, amplitude, intercom, } ) => {
	const prefix = "Member Onboarding -";

	const personalGrowthOpportunityV3Text = {
		[ MemberGrowthOpportunitiesV3.CAREER_PLAN ]: "developing a career plan",
		[ MemberGrowthOpportunitiesV3.WORK_PRIORITY ]: "improving communication about work priorities",
		[ MemberGrowthOpportunitiesV3.BURNOUT ]: "avoiding burnout",
		[ MemberGrowthOpportunitiesV3.DIFFICULT_CONVERSATIONS ]: "handling difficult conversations",
		[ MemberGrowthOpportunitiesV3.SOLICIT_FEEDBACK ]: "soliciting more feedback",
		[ MemberGrowthOpportunitiesV3.RELATIONSHIPS ]: "building good relationships with teammates",
		[ MemberGrowthOpportunitiesV3.WORK_VALUE ]: "understanding the impact and value of my work",
	};

	return {
		started( teamId ) {
			intercom.update( currentUser(), {
				onboarding_path_type: OnboardingType.MEMBER,
			} );
			amplitude.event( "Onboarding Started", {
				path: "Invitee",
				associated_team: teamId,
			} );
		},
		completed( teamId ) {
			amplitude.event( "Onboarding Completed", {
				path: "Invitee",
				associated_team: teamId,
			} );
			intercom.update( currentUser(), {
				onboarding_completed: true,
			} );
		},
		welcome() {
			amplitude.event( `${ prefix } Welcome`, {}, true );
		},
		/** @deprecated */
		discoverWorkStyle() {
			amplitude.event( `${ prefix } Discover Work Style` );
		},
		/** @deprecated */
		workStyleAssessment() {
			amplitude.event( `${ prefix } Work Style Assessment` );
		},
		/** @deprecated */
		workStyleResults() {
			amplitude.event( `${ prefix } Work Style Results` );
		},
		/** @deprecated */
		workStyleComparison() {
			amplitude.event( `${ prefix } Work Style Comparison` );
		},
		/** @deprecated */
		priority() {
			amplitude.event( `${ prefix } Priority` );
		},
		/** @deprecated */
		questions() {
			amplitude.event( `${ prefix } Questions` );
		},
		/** @deprecated */
		calendarIntegration() {
			amplitude.event( `${ prefix } Calendar Integration` );
		},
		growthOpportunities() {
			amplitude.event( `${ prefix } Personal Growth Priority`, {
				location: "Member Onboarding",
			}, true );
		},
		growthOpportunity( { growthOpportunity, onboardingVersion = 3, } ) {
			const textMaps = {
				3: personalGrowthOpportunityV3Text,
			};
			const textMap = textMaps[ onboardingVersion ] || {};
			const text = textMap[ growthOpportunity ];

			if ( !text ) {
				// eslint-disable-next-line no-console
				return console.warn( `missing growth opportunity text for ${ growthOpportunity }; will not update Intercom` );
			}

			intercom.update( currentUser(), {
				team_member_personal_growth_priority: growthOpportunity,
				team_member_personal_growth_priority_display_text: text,
			} );
		},
		/** @deprecated */
		workStyleAssessmentStarted() {
			amplitude.event( "Work Style Assessment Started", {
				location: "Member Onboarding",
			} );
		},
		/** @deprecated */
		workStyleAssessmentCompleted() {
			amplitude.event( "Work Style Assessment Completed", {
				location: "Member Onboarding",
			} );
		},
	};
};

export default ( { currentUser, amplitude, intercom, } ) => {
	return {
		calendarAuthorizationInitiated( calendarProvider, location ) {
			amplitude.event( "Calendar Authorization Initiated", {
				calendar_provider: calendarProvider,
				location,
			} );
		},
		calendarAuthorizationComplete( calendarProvider, status, location ) {
			amplitude.event( "Calendar Authorization Complete", {
				calendar_provider: calendarProvider,
				status,
				location,
			} );
		},
		teamMemberInvited( teamName, email ) {
			amplitude.event( "Team Member Invited", {
				team_name: teamName,
				invited_user_email: email,
				location: "Onboarding",
			} );
		},
		lead: leadFactory( { currentUser, amplitude, intercom, } ),
		member: memberFactory( { currentUser, amplitude, intercom, } ),
	};
};
